import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, API, formatApiError, formatDate, formatRelative, openAuthedPdf } from "../lib/api";
import { fmtMoney } from "../components/ops/opsUtils";
import {
  ArrowLeft, Download, Send, CheckCircle2, XCircle, Clock, FileSignature, Loader2,
  History as HistoryIcon, Mail, AlertTriangle, Eye,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const STATUS_BADGES = {
  draft:        { bg: "#F3F4F6", text: "#4B5563", label: "Draft",        Icon: FileSignature },
  sent:         { bg: "#EFF6FF", text: "#1D4ED8", label: "Sent",         Icon: Send },
  viewed:       { bg: "#FEF3C7", text: "#B45309", label: "Viewed",       Icon: Eye },
  signed:       { bg: "#DCFCE7", text: "#166534", label: "Signed",       Icon: CheckCircle2 },
  completed:    { bg: "#DCFCE7", text: "#166534", label: "Completed",    Icon: CheckCircle2 },
  funding_sent: { bg: "#DBEAFE", text: "#1E40AF", label: "Funding Sent", Icon: CheckCircle2 },
  voided:       { bg: "#FEE2E2", text: "#B91C1C", label: "Voided",       Icon: XCircle },
};

const FLOW = ["draft", "sent", "viewed", "signed", "completed", "funding_sent"];

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/operations/contracts/${id}`);
      setContract(data);
    } catch (e) { toast.error(formatApiError(e)); navigate("/admin/contracts"); }
    finally { setLoading(false); }
  };

  const downloadPdf = async () => {
    try { await openAuthedPdf(`/operations/contracts/${id}/pdf`, `${id}.pdf`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const sendForSignature = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/operations/contracts/${id}/send`, {});
      if (data.docusign_configured) {
        toast.success(`Sent via DocuSign (envelope ${data.envelope_id})`);
      } else {
        toast.success(`Sent via email fallback to ${data.recipients?.join(", ") || "configured recipients"} — DocuSign not yet configured`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const advance = async (status, note = "") => {
    try {
      await api.patch(`/operations/contracts/${id}/status`, { status, note });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const voidIt = async () => {
    if (!window.confirm("Void this agreement?")) return;
    try {
      await api.post(`/operations/contracts/${id}/void`);
      toast.success("Voided");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="p-10 text-[#6B7280]" data-testid="contract-loading">Loading…</div>;
  if (!contract) return null;

  const status = contract.status;
  const cfg = STATUS_BADGES[status] || STATUS_BADGES.draft;
  const StatusIcon = cfg.Icon;
  const econ = contract.economics_snapshot || {};
  const merge = contract.merge_snapshot || {};

  return (
    <div className="p-6 md:p-8 max-w-[1300px] mx-auto" data-testid="contract-detail">
      <Link to="/admin/contracts" className="inline-flex items-center text-sm text-[#6B7280] hover:text-[#0A2540] mb-3" data-testid="back-to-contracts">
        <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> All agreements
      </Link>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0A2540] to-[#0066FF] rounded-2xl p-6 md:p-8 text-white mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#BFD7FF] mb-2 flex items-center gap-2">
              <FileSignature className="w-3 h-3" /> Nonrecourse Purchase Agreement · {contract.contract_number}
            </div>
            <div className="font-display text-3xl md:text-4xl font-semibold tracking-tight">{contract.client_name}</div>
            <div className="text-sm text-[#BFD7FF] mt-1">
              {contract.attorney_name || contract.attorney_email}{contract.attorney_firm ? ` · ${contract.attorney_firm}` : ""}
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: cfg.bg, color: cfg.text }}>
            <StatusIcon className="w-3.5 h-3.5" /> {cfg.label}
          </span>
        </div>
        <div className="grid sm:grid-cols-4 gap-4 pt-5 border-t border-white/15">
          <Mini label="Funding amount" value={`$${econ.principal?.toLocaleString?.() || merge.funding_amount || "—"}`} big />
          <Mini label="Monthly rate" value={`${merge.monthly_rate_pct || ""}%`} />
          <Mini label="Max ownership" value={`$${merge.max_ownership || "—"}`} />
          <Mini label="Min payment" value={`$${merge.min_payment || "—"}`} />
        </div>
        <div className="flex flex-wrap gap-2 mt-5">
          <Button onClick={downloadPdf} className="bg-white text-[#0A2540] hover:bg-[#F9FAFB] h-9" data-testid="contract-download-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
          </Button>
          {status === "draft" && (
            <Button onClick={sendForSignature} disabled={sending} className="bg-[#0066FF] hover:bg-white/15 border border-white/30 text-white h-9" data-testid="contract-send-btn">
              {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Send for signature
            </Button>
          )}
          {["sent", "viewed"].includes(status) && (
            <Button onClick={() => advance("signed", "Marked signed manually")} className="bg-white/15 hover:bg-white/25 text-white h-9" data-testid="contract-mark-signed-btn">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark signed
            </Button>
          )}
          {status === "signed" && (
            <Button onClick={() => advance("completed", "All counterparts signed")} className="bg-white/15 hover:bg-white/25 text-white h-9" data-testid="contract-complete-btn">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark completed
            </Button>
          )}
          {status === "completed" && (
            <Button onClick={() => advance("funding_sent", "Funds disbursed")} className="bg-white/15 hover:bg-white/25 text-white h-9" data-testid="contract-funding-sent-btn">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark funding sent
            </Button>
          )}
        </div>
      </div>

      {/* DocuSign banner */}
      {!contract.docusign_envelope_id && status !== "voided" && (
        <div className="bg-[#FFF7E6] border border-[#FCD37C] rounded-lg p-3 mb-5 flex items-center gap-3 text-sm" data-testid="docusign-banner">
          <AlertTriangle className="w-4 h-4 text-[#B45309] flex-shrink-0" />
          <div className="flex-1 text-[#0A2540]">
            <span className="font-medium">DocuSign not yet configured.</span> The system is using a download/email fallback. Add your DocuSign keys to <code className="text-[11px] bg-white px-1.5 py-0.5 rounded">backend/.env</code> to enable native e-signature routing.
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-5">
          {/* Ownership schedule */}
          <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-baseline justify-between">
              <div className="font-medium text-[#0A2540]">Ownership Schedule</div>
              <span className="text-xs text-[#6B7280]">Computed from contract economics</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
                  <th className="px-4 py-2.5 font-medium">Tier</th>
                  <th className="px-4 py-2.5 font-medium">Start</th>
                  <th className="px-4 py-2.5 font-medium">End</th>
                  <th className="px-4 py-2.5 font-medium text-right">Ownership amount</th>
                </tr>
              </thead>
              <tbody>
                {(econ.ownership_schedule || []).map((t) => (
                  <tr key={t.tier} className="border-b border-[#F3F4F6]" data-testid={`tier-${t.tier}`}>
                    <td className="px-4 py-2.5 text-[#0A2540] font-medium">Tier {t.tier}</td>
                    <td className="px-4 py-2.5 text-[#6B7280] tabular-nums">{formatDate(t.start_date)}</td>
                    <td className="px-4 py-2.5 text-[#6B7280] tabular-nums">{formatDate(t.end_date)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-[#0A2540] font-medium">${(t.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#F9FAFB]">
                <tr className="text-sm">
                  <td colSpan={3} className="px-4 py-3 text-[11px] uppercase tracking-wider text-[#6B7280]">Maximum ownership (cap)</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#B45309] font-semibold">${(econ.max_ownership || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Status timeline */}
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <div className="font-medium text-[#0A2540] mb-3 flex items-center gap-2"><HistoryIcon className="w-3.5 h-3.5" /> Lifecycle</div>
            <div className="flex items-center gap-1 mb-5">
              {FLOW.map((s, i) => {
                const reached = FLOW.indexOf(status) >= i;
                const current = status === s;
                return (
                  <React.Fragment key={s}>
                    <div className={`flex flex-col items-center text-[10px] uppercase tracking-wider ${reached ? "text-[#0066FF] font-medium" : "text-[#9CA3AF]"}`} data-testid={`flow-step-${s}`}>
                      <div className={`w-3 h-3 rounded-full mb-1 ${reached ? (current ? "bg-[#0066FF] ring-2 ring-[#0066FF]/30" : "bg-[#0066FF]") : "bg-[#E5E7EB]"}`} />
                      {s.replace("_", " ")}
                    </div>
                    {i < FLOW.length - 1 && <div className={`flex-1 h-px ${reached && FLOW.indexOf(status) > i ? "bg-[#0066FF]" : "bg-[#E5E7EB]"}`} />}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="space-y-2">
              {[...(contract.status_history || [])].reverse().map((h, i) => (
                <div key={`hist-${h.at || i}`} className="text-xs flex items-start gap-2 pb-2 border-b border-[#F3F4F6] last:border-b-0" data-testid={`history-${i}`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-1.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-[#0A2540]"><span className="font-medium">{h.status}</span>{h.note ? <span className="text-[#6B7280]"> — {h.note}</span> : ""}</div>
                    <div className="text-[10px] text-[#9CA3AF] mt-0.5 tabular-nums">{formatRelative(h.at)} · {h.by}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-3">Linked records</div>
            <Link to={`/admin/loans/${contract.loan_id}`} className="block p-2.5 rounded-md hover:bg-[#F9FAFB] mb-1" data-testid="link-loan">
              <div className="text-xs text-[#6B7280]">Loan</div>
              <div className="text-sm font-medium text-[#0066FF]">{contract.loan_id}</div>
            </Link>
            <Link to={`/admin/clients/${encodeURIComponent(contract.client_name)}`} className="block p-2.5 rounded-md hover:bg-[#F9FAFB] mb-1" data-testid="link-client">
              <div className="text-xs text-[#6B7280]">Client</div>
              <div className="text-sm font-medium text-[#0066FF]">{contract.client_name}</div>
            </Link>
            {contract.attorney_email && (
              <Link to={`/admin/attorneys/${encodeURIComponent(contract.attorney_email)}`} className="block p-2.5 rounded-md hover:bg-[#F9FAFB]" data-testid="link-attorney">
                <div className="text-xs text-[#6B7280]">Attorney</div>
                <div className="text-sm font-medium text-[#0066FF]">{contract.attorney_email}</div>
              </Link>
            )}
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">Quick actions</div>
            <Button onClick={downloadPdf} variant="outline" className="w-full justify-start h-9" data-testid="quick-download">
              <Download className="w-3.5 h-3.5 mr-2" /> Download PDF
            </Button>
            {status !== "voided" && status !== "completed" && status !== "funding_sent" && (
              <Button onClick={voidIt} variant="outline" className="w-full justify-start h-9 text-[#B91C1C] border-[#FECACA] hover:bg-[#FEF2F2]" data-testid="quick-void">
                <XCircle className="w-3.5 h-3.5 mr-2" /> Void agreement
              </Button>
            )}
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-3">Document details</div>
            <Row label="Template" value={contract.template_id} />
            <Row label="Version" value={contract.template_version} />
            <Row label="Doc ID" value={contract.doc_id} />
            <Row label="Generated" value={formatDate(contract.generated_at)} />
            <Row label="By" value={contract.generated_by} />
            {contract.signed_at && <Row label="Signed" value={formatDate(contract.signed_at)} />}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Mini({ label, value, big }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#BFD7FF] mb-1">{label}</div>
      <div className={`tabular-nums font-medium ${big ? "font-display text-2xl" : "text-base"}`}>{value}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="py-1.5 flex justify-between text-sm border-t border-[#F3F4F6] first:border-t-0">
      <span className="text-[11px] uppercase tracking-wider text-[#6B7280]">{label}</span>
      <span className="text-[#0A2540] tabular-nums">{value || "—"}</span>
    </div>
  );
}
